// @ts-nocheck
import { JxsComponent } from '@jxs/component';
import { jxsAnimate } from '@jxs/gsap';
import { JxsSwiperSlideChange } from '@jxs/utilities';

const Swiper = /** @type {any} */ (window).Swiper;

const JXS_SLIDESHOW_ITEM_TIMEUPDATE_EVENT = 'jxs-slideshow-item:timeupdate';
const JXS_SLIDESHOW_ITEM_ENDED_EVENT = 'jxs-slideshow-item:ended';
const JXS_SLIDESHOW_CHANGE_EVENT = 'slideshowChange';
const JXS_SLIDESHOW_MOBILE_MEDIA_QUERY = '(max-width: 767px)';

/**
 * @typedef {object} JxsSlideshowItemRefs
 * @property {HTMLElement & {
 *   play?: () => Promise<void> | void,
 *   pause?: () => void,
 *   duration?: number,
 *   loop?: boolean
 * }} [playable]
 *
 * @typedef {object} JxsSlideshowItemEventDetail
 * @property {JxsSlideshowItem} item
 * @property {HTMLElement | null} playable
 *
 * @extends {JxsComponent<JxsSlideshowItemRefs>}
 */
export class JxsSlideshowItem extends JxsComponent {
  #playable = null;

  #setupOptions = null;

  #videoReadyStateVersion = 0;

  #onPlayableEnd = (event) => {
    this.dispatchEvent(new CustomEvent(JXS_SLIDESHOW_ITEM_ENDED_EVENT, {
      bubbles: true,
      cancelable: false,
      detail: {
        item: this,
        playable: event.currentTarget
      }
    }));
  }

  #onPlayableProgressChanged = (event) => {
    this.dispatchEvent(new CustomEvent(JXS_SLIDESHOW_ITEM_TIMEUPDATE_EVENT, {
      bubbles: true,
      cancelable: false,
      detail: {
        item: this,
        playable: event.currentTarget
      }
    }));
  }

  #onPlayablePlaying = (event) => {
    this.#showVideoAfterFirstFrame(event.currentTarget);
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#setupOptions) {
      this.setupPlayable(this.#setupOptions);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroyPlayable();
  }

  prepareResponsiveVideo() {
    const container = this.querySelector(':scope > .jxs-slideshow-item__media [data-jxs-slideshow-responsive-video]');
    if (!(container instanceof HTMLElement)) return;

    if (container.querySelector('[ref="playable"]')) return;

    const isMobile = window.matchMedia?.(JXS_SLIDESHOW_MOBILE_MEDIA_QUERY).matches ?? window.innerWidth < 768;
    const preferredTemplateType = isMobile ? 'mobile' : 'desktop';
    const fallbackTemplateType = isMobile ? 'desktop' : 'mobile';
    const template =
      container.querySelector(`template[data-jxs-slideshow-video-template="${preferredTemplateType}"]`) ||
      container.querySelector(`template[data-jxs-slideshow-video-template="${fallbackTemplateType}"]`);

    if (!(template instanceof HTMLTemplateElement)) return;

    container.append(template.content.cloneNode(true));
    this.updatedCallback();
  }

  get playable() {
    return this.refs.playable || this.querySelector(':scope > .jxs-slideshow-item__media [ref="playable"]') || null;
  }

  /**
   * 将 item 内部的 playable 初始化为 slideshow 可控制的播放源。
   *
   * @param {{ duration?: number, loop?: boolean, autoplay?: boolean }} [options]
   */
  setupPlayable(options = {}) {
    this.#setupOptions = { ...options };
    this.destroyPlayable();
    this.prepareResponsiveVideo();

    if (options.autoplay === false && this.#unwrapVideoSimulation()) {
      return;
    }

    const playable = this.playable;
    if (!playable) return;

    this.#playable = playable;

    if (playable instanceof HTMLMediaElement) {
      playable.muted = true;
      playable.defaultMuted = true;
      playable.playsInline = true;
      playable.setAttribute('playsinline', '');
      playable.setAttribute('webkit-playsinline', '');
    }

    const duration = Number(options.duration);
    if (!(playable instanceof HTMLMediaElement) && Number.isFinite(duration) && duration > 0) {
      try {
        playable.duration = duration;
      } catch (error) {
        console.warn('Failed to set slideshow item playable duration.', error);
      }
    }

    if (options.loop !== undefined) {
      try {
        playable.loop = Boolean(options.loop);
      } catch (error) {
        console.warn('Failed to set slideshow item playable loop.', error);
      }
    }

    playable.addEventListener('timeupdate', this.#onPlayableProgressChanged);
    playable.addEventListener('ended', this.#onPlayableEnd);
    playable.addEventListener('playing', this.#onPlayablePlaying);
  }

  #unwrapVideoSimulation() {
    const playable = this.playable;

    if (!(playable instanceof HTMLElement) || playable.localName !== 'jxs-video-simulation') {
      return false;
    }

    const container = playable.parentElement;
    if (!container) return false;

    if (typeof playable.pause === 'function') {
      playable.pause();
    }

    const fragment = document.createDocumentFragment();

    while (playable.firstChild) {
      fragment.append(playable.firstChild);
    }

    container.insertBefore(fragment, playable);
    playable.remove();
    this.updatedCallback();

    return true;
  }

  destroyPlayable() {
    if (!this.#playable) return;

    this.#playable.removeEventListener('timeupdate', this.#onPlayableProgressChanged);
    this.#playable.removeEventListener('ended', this.#onPlayableEnd);
    this.#playable.removeEventListener('playing', this.#onPlayablePlaying);
    this.#playable = null;
  }

  #setVideoReadyState(playable, isReady) {
    if (!isReady) {
      this.#videoReadyStateVersion += 1;
    }

    playable
      ?.closest?.('[data-jxs-slideshow-responsive-video]')
      ?.toggleAttribute('data-jxs-slideshow-video-ready', isReady);
  }

  #showVideoAfterFirstFrame(playable) {
    const videoReadyStateVersion = this.#videoReadyStateVersion;

    const setVideoReady = () => {
      if (videoReadyStateVersion !== this.#videoReadyStateVersion) return;
      if (playable instanceof HTMLMediaElement && playable.paused) return;

      this.#setVideoReadyState(playable, true);
    }

    if (!(playable instanceof HTMLVideoElement)) {
      setVideoReady();
      return;
    }

    if (typeof playable.requestVideoFrameCallback === 'function') {
      playable.requestVideoFrameCallback(setVideoReady);
      return;
    }

    requestAnimationFrame(setVideoReady);
  }

  play() {
    const playable = this.#playable || this.playable;
    if (!playable || typeof playable.play !== 'function') return;

    this.#setVideoReadyState(playable, false);

    if (playable instanceof HTMLMediaElement) {
      playable.muted = true;
      playable.defaultMuted = true;
      playable.playsInline = true;
      playable.setAttribute('playsinline', '');
      playable.setAttribute('webkit-playsinline', '');
    }

    try {
      playable.currentTime = 0;
    } catch (error) {
      console.warn('Failed to reset slideshow item playable.', error);
    }

    const result = playable.play();

    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('Failed to play slideshow item playable.', error);
      });
    }
  }

  pause() {
    const playable = this.#playable || this.playable;
    if (!playable || typeof playable.pause !== 'function') return;

    playable.pause();
    this.#setVideoReadyState(playable, false);
  }

  setRealIndex(index) {
    this.dataset.realIndex = index
  }

  getRealIndex() {
    return this.getDataset('realIndex')
  }
}

/**
 * JxsSlideshow - 基于 Swiper 的轮播组件。
 *
 * 组件职责：
 * - 初始化 Swiper、分页和进度条；
 * - 控制当前 item 内部的视频或模拟播放源；
 * - 在自动播放模式下，根据当前 item 播放结束切换到下一屏；
 * - 在 loop + 非 slide effect 场景下，避免 Swiper 拖拽期间多次移动 DOM 导致视频被过早 pause/play。
 *
 * 重要约定：
 * 不把 Swiper 的 slideChange 直接当作业务切换完成事件。
 * Swiper slideChange 只代表视觉层 active slide 发生变化，尤其在 cube/fade 等 effect 与 loop 同时开启时，
 * 拖拽过程中可能出现多个中间态。业务层统一通过内部 slideshowChange 事件驱动播放控制。
 *
 * @typedef {object} JxsSlideshowRefs
 * @property {JxsSlideshowItem[]} items
 * @property {HTMLElement} swiper
 * @property {HTMLElement} pagination
 *
 * @typedef {object} JxsSlideshowEventData
 * @property {string} name
 *
 * @extends {JxsComponent<JxsSlideshowRefs>}
 */
export class JxsSlideshow extends JxsComponent {
  #swiper = null;

  #swiperSlideChange = null;

  #paginationItems = [];

  #progressBars = [];

  /**
   * 业务层确认的当前 item index。
   *
   * 不直接使用 Swiper activeIndex：
   * - loop 模式下 activeIndex 会包含 duplicated slide；
   * - cube/fade 等 effect 在拖拽过程中可能反复触发 slideChange；
   * - 业务播放逻辑只应该响应最终确认后的真实 item 变化。
   */
  #activeIndex = 0;

  /**
   * 当前 slideshow 的视口监听清理函数。
   */
  #inViewCleanup = null;

  /**
   * Slideshow 自己的业务 change 事件消费者。
   *
   * Swiper 的 slideChange 只表示视觉层 active slide 变化；
   * 这里的 slideshowChange 才表示业务层确认 item 已切换。
   */
  #onSlideshowChange = (event) => {
    const index = Number(event.detail?.index);

    this.#updateArrowStatus();
    this.#stopAllPlayable();
    this.#setActivePaginationItem(index);
    this.#playItem(index);
  }

  /**
   * 当前 item 播放结束后的处理。
   *
   * 自动播放开启时，播放结束才推动 Swiper 到下一屏；
   * 自动播放关闭时，item 自身会按 loop 设置循环或停留。
   */
  #onItemEnd = (event) => {
    const item = event.currentTarget;

    if (item !== this.getCurrentSlideshowItem()) {
      this.#updateProgressBarByItem(item, 0);
      return;
    }

    this.#updateProgressBarByItem(item, 100);

    if (this.isAutoplay) {
      item.pause();
      if (this.#swiperSlideChange.isTouching) {
        item.play();
        return;
      }

      if (this.#activeIndex == this.refs.items.length - 1) {
        this.#swiper.slideToLoop(0);
      } else {
        this.#swiper.slideNext();
      }
    }
  }

  /**
   * 接收 item 内部 playable 的 timeupdate，并同步对应分页进度条。
   */
  #onItemProgressChanged = (event) => {
    const item = event.currentTarget;

    if (item !== this.getCurrentSlideshowItem()) {
      this.#updateProgressBarByItem(item, 0);
      return;
    }

    this.#updateProgressBarByItem(item, event.detail?.playable);
  }

  /**
   * 分页点击时通过 slideToLoop 跳转。
   *
   * 使用 slideToLoop 而不是 slideTo，是为了在 loop 模式下按真实 item index 跳转。
   */
  #onPageClick = (event) => {
    this.#swiper.slideToLoop(event.currentTarget.dataset.index);
  }

  constructor() {
    super();

    // 是否启用“当前 item 播放完再切下一屏”的自动播放模式。
    this.isAutoplay = this.getDataset('autoplay');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(JXS_SLIDESHOW_CHANGE_EVENT, this.#onSlideshowChange);

    const items = this.refs.items || [];
    if (items.length == 0) return;

    if (items.length > 1) {
      const firstItem = items[0];
      if (this.dataset.desktopHeight == 'auto' && firstItem.dataset.desktopRatio) {
        this.style.setProperty('--slideshow-ratio-desktop', firstItem.dataset.desktopRatio);
      }
      if (this.dataset.mobileHeight == 'auto' && firstItem.dataset.mobileRatio) {
        this.style.setProperty('--slideshow-ratio-mobile', firstItem.dataset.mobileRatio);
      }
      this.initSlider();
      this.initPagination();
    }

    this.#setupItemsAfterUpgrade();
  }

  /**
   * 等待 slideshow item 自定义元素完成升级后，再初始化和播放每个 item。
   *
   * 背景：
   * Shopify 主题编辑器修改 section/block 设置后，会动态替换或重新插入 section 的 HTML。
   * 在这个过程中，父组件 <jxs-slideshow> 的 connectedCallback 可能已经执行，
   * 并且 this.refs.items 也已经能收集到 <jxs-slideshow-item> 节点；
   * 但这些子节点有可能还只是普通 HTMLElement，尚未升级为 JxsSlideshowItem 实例。
   *
   * 如果此时直接调用 item.play()，会出现：
   *   TypeError: item.play is not a function
   *
   * 因此，Swiper 和 pagination 这类只依赖 DOM 的初始化可以先执行；
   * 而 setupPlayable/play 等依赖 JxsSlideshowItem 实例方法的逻辑，需要放到这里，
   * 等 customElements.whenDefined('jxs-slideshow-item') 完成，并确认 item 方法可用后再执行。
   */
  async #setupItemsAfterUpgrade() {
    await customElements.whenDefined('jxs-slideshow-item');

    if (!this.isConnected) return;

    const items = this.refs.items || [];
    if (items.length == 0) return;

    // 如果 Shopify 的动态插入时序比较特殊，可以强制 upgrade 一下当前 subtree
    customElements.upgrade(this);

    // 重新确认 item 方法已经存在
    if (items.some(item => typeof item.play !== 'function')) {
      requestAnimationFrame(() => {
        this.#setupItemsAfterUpgrade();
      });
      return;
    }

    items.forEach((item, index) => {
      item.setRealIndex(index);
    });

    this.#activeIndex = this.#swiper?.realIndex ?? 0;
    this.#setupItemsForAutoplay();
    this.#setActivePaginationItem(this.#activeIndex);
    this.#updateArrowStatus();

    this.#inViewCleanup?.();
    this.#inViewCleanup = jxsAnimate.inView({
      el: this,
      enterCallback: () => {
        this.getCurrentSlideshowItem()?.play?.();
      },
    });
  }


  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(JXS_SLIDESHOW_CHANGE_EVENT, this.#onSlideshowChange);
    this.#inViewCleanup?.();
    this.#inViewCleanup = null;
    this.#swiperSlideChange?.destroy();
    this.#swiperSlideChange = null;
    this.#swiper?.destroy(true, true);
    this.refs.items?.forEach(item => {
      item.removeEventListener(JXS_SLIDESHOW_ITEM_TIMEUPDATE_EVENT, this.#onItemProgressChanged);
      item.removeEventListener(JXS_SLIDESHOW_ITEM_ENDED_EVENT, this.#onItemEnd);
      item.destroyPlayable?.();
    });
    this.#paginationItems.forEach(pageItem => {
      pageItem.removeEventListener('click', this.#onPageClick);
    });
  }

  /**
   * 初始化 Swiper，并把 Swiper 事件转换为 slideshow 业务切换。
   *
   * 这里是修复 loop + cube/fade 等 effect 视频播放异常的关键：
   * - 手势拖拽期间不做 pause/play，只记录最后一次 realIndex；
   * - touchEnd 后再提交最终 realIndex；
   * - 分页点击、自动播放、API 调用等非手势切换则立即提交。
   */
  initSlider() {
    this.#swiper = new Swiper(this.refs.swiper, {
      loop: this.getDataset('loop'),
      autoplay: false,
      rewind: false,
      slidesPerView: 1,
      effect: this.dataset.switchType
    });

    this.#swiperSlideChange = new JxsSwiperSlideChange(this.#swiper);
    this.#swiperSlideChange.addListener((swiper, previousIndex, index) => {
      this.#handleSliderChange(index);
    });
  }

  prevItem() {
    this.#swiper.slidePrev();
  }

  nextItem() {
    this.#swiper.slideNext();
  }

  getCurrentSlideshowItem() {
    return this.findSlideshowItem(this.#activeIndex);
  }

  findSlideshowItem(index) {
    return this.refs.items.find(item => item.getRealIndex() == index);
  }

  /**
   * 根据 item 数量重建分页 DOM。
   *
   * pageType 为 progress 时，每个分页项会附带一个 jxs-progress-bar，
   * 后续由 item 的 timeupdate / ended 事件驱动进度显示。
   */
  initPagination() {
    this.#paginationItems = [];
    this.#progressBars = [];
    this.refs.pagination.replaceChildren();

    this.refs.items.forEach((_, index) => {
      const item = document.createElement('div');
      item.dataset.index = index;
      item.classList.add('jxs-slideshow__pagination-item');
      item.toggleAttribute('active', index === 0);
      if (this.getDataset('pageClickable')) {
        item.addEventListener('click', this.#onPageClick);
      }
      this.refs.pagination.append(item);
      this.#paginationItems.push(item);
      
      if (this.dataset.pageType == 'progress') {
        const progressBar = document.createElement('jxs-progress-bar');
        item.append(progressBar);
        this.#progressBars.push(progressBar);
      }
    });
  }

  /**
   * 初始化每个 item 的 playable，并按自动播放配置绑定进度和结束事件。
   */
  #setupItemsForAutoplay() {
    const autoplaySec = Number(this.getDataset('autoplaySec'));
    const autoplayDuration = Number.isFinite(autoplaySec) ? autoplaySec * 1000 : undefined;

    this.refs.items.forEach(item => {
      item.setupPlayable?.({
        duration: autoplayDuration,
        loop: !this.isAutoplay,
        autoplay: this.isAutoplay
      });
      if (this.isAutoplay) {
        item.addEventListener(JXS_SLIDESHOW_ITEM_TIMEUPDATE_EVENT, this.#onItemProgressChanged);
        item.addEventListener(JXS_SLIDESHOW_ITEM_ENDED_EVENT, this.#onItemEnd);
      }
    });
  }

  /**
   * 停止所有 item 的播放源，并把所有进度条重置为 0。
   *
   * 在业务切换开始时统一调用，避免旧 item 的视频继续播放。
   */
  #stopAllPlayable() {
    this.refs.items.forEach((item, index) => {
      item.pause?.();
      this.#progressBars[index]?.update(0);
    })
  }

  /**
   * 将 Swiper 的 realIndex 变化转换为 slideshow 业务 change。
   *
   * 只有当真实 item index 和 #activeIndex 不同时才派发事件。
   * 这样可以过滤 loop/effect/初始化修正产生的重复 slideChange。
   *
   * @param {number | string} index
   */
  #handleSliderChange(index) {
    index = Number(index);

    if (!Number.isInteger(index) || index === this.#activeIndex) return;

    const previousIndex = this.#activeIndex;
    this.#activeIndex = index;

    this.dispatchEvent(new CustomEvent(JXS_SLIDESHOW_CHANGE_EVENT, {
      bubbles: true,
      cancelable: false,
      detail: {
        index,
        previousIndex
      }
    }));
  }

  /**
   * 播放指定真实 item index 对应的 item。
   *
   * @param {number} index
   */
  #playItem(index) {
    this.findSlideshowItem(index)?.play?.();
  }

  /**
   * 更新分页 active 状态，并处理非当前项进度重置。
   *
   * 非自动播放模式下，当前分页项固定显示 100%，表示当前静态 item 已展示完成。
   *
   * @param {number} index
   */
  #setActivePaginationItem(index) {
    this.#paginationItems.forEach((item, itemIndex) => {
      item.toggleAttribute('active', itemIndex === index);
      if (this.getDataset('pageType') == 'progress') {
        this.#progressBars[itemIndex]?.update(itemIndex !== index ? 0 : 100);
      }
    });
  }

  /**
   * 根据 item 反查进度条，并同步进度值。
   *
   * @param {JxsSlideshowItem} item
   * @param {number | HTMLElement | null | undefined} value
   */
  #updateProgressBarByItem(item, value) {
    if (this.getDataset('pageType') == 'progress') {
      const index = Number(item.getRealIndex());
      this.#progressBars[index]?.update(value);
    }
  }

  #updateArrowStatus() {
    if (!this.getDataset('loop')) {
      this.refs.prevButton.disabled = this.#activeIndex == 0
      this.refs.nextButton.disabled = this.#activeIndex == this.refs.items.length - 1;
    }
  }
}

if (!customElements.get('jxs-slideshow-item')) {
  customElements.define('jxs-slideshow-item', JxsSlideshowItem)
}

if (!customElements.get('jxs-slideshow')) {
  customElements.define('jxs-slideshow', JxsSlideshow)
}